import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Wrench, Siren, Navigation, Radio, Menu, X, MoreVertical, Pencil, MapPin, MapPinOff, Loader2, RefreshCw, WifiOff, AlertTriangle, Copy, Layers, LocateFixed, Bell, BellOff, SlidersHorizontal, Volume2, Circle, BatteryFull, BatteryMedium, BatteryLow, BatteryWarning, Maximize2, Minimize2, Trash2, Crosshair } from "lucide-react";
import { toast } from "sonner";
import HomeMap from "@/components/HomeMap";
import LocationPreviewMap from "@/components/LocationPreviewMap";
import OverlayManager from "@/components/OverlayManager";
import LayersMenu from "@/components/LayersMenu";
import { useHomeOverlay } from "@/hooks/useHomeOverlay";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";

const POLL_MS = 5000;
const SETUP_URL = "https://setup.makrolog.page";
const ACK_TTL = 900; // green "Alarm beendet" stays for 15 minutes after the acknowledgement

function clockOf(epoch) {
  return new Date(epoch * 1000).toLocaleTimeString("de-DE", {
    timeZone: "Europe/Berlin",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
function clockHM(epoch) {
  return new Date(epoch * 1000).toLocaleTimeString("de-DE", {
    timeZone: "Europe/Berlin",
    hour: "2-digit",
    minute: "2-digit",
  });
}
// "vor X Minute(n)" — only once at least a minute has passed.
function vorMinuten(sec) {
  const m = Math.floor(Math.max(0, sec) / 60);
  if (m < 1) return "";
  return m === 1 ? "vor 1 Minute" : `vor ${m} Minuten`;
}
function seitDauer(sec) {
  const total = Math.floor(Math.max(0, sec) / 60); // total minutes
  if (total < 1) return "gerade eben";
  if (total < 60) return total === 1 ? "seit 1 Minute" : `seit ${total} Minuten`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h < 24) {
    if (m > 0) return `seit ${h} Std ${m} Min`;
    return h === 1 ? "seit 1 Stunde" : `seit ${h} Stunden`;
  }
  const day = Math.floor(h / 24);
  const hr = h % 24;
  if (hr > 0) return `seit ${day} Tg ${hr} Std`;
  return day === 1 ? "seit 1 Tag" : `seit ${day} Tagen`;
}
// "Check: vor X Min" — time since the object's last message (device health).
function checkText(sec) {
  if (sec == null) return "Check: —";
  const m = Math.floor(Math.max(0, sec) / 60);
  if (m < 1) return "Check: jetzt";
  return m === 1 ? "Check: vor 1 Min" : `Check: vor ${m} Min`;
}
// "stationär seit X Minute(n)" helper removed — trackers now show just "stationär".

// "92% (noch 12 h)" — battery percent + estimated remaining runtime. The "(noch …)"
// part only appears once the backend has learned a drain rate (a real drop over time).
function batteryText(pct, hours) {
  if (pct == null) return null;
  if (hours == null) return `${pct}%`;
  if (hours >= 48) return `${pct}% (noch ${Math.round(hours / 24)} Tage)`;
  if (hours >= 1) return `${pct}% (noch ${Math.round(hours)} h)`;
  return `${pct}% (noch <1 h)`;
}

const ALARM_RED_TTL = 600; // active SOS shows red + "vor X Minuten" for the first 10 minutes
const ALARM_TTL = 3600; // after the red phase an old alarm is click-only, gone after 1 hour
const STALE_TTL = 600; // tracker text turns grey (and its trail is hidden) after 10 min
const SIGNAL_TTL = 300; // no ACT beacon for > 5 min -> subtle "kein Signal" (possible Funkloch)

// Distinct colors for simultaneously-moving Streifen: 1st blue, 2nd green,
// 3rd yellow, 4th red, then further well-separated hues.
const MOVE_COLORS = ["#2563eb", "#16a34a", "#eab308", "#dc2626", "#9333ea", "#0d9488", "#ea580c", "#db2777"];
function moveColorAt(i) {
  if (i < MOVE_COLORS.length) return MOVE_COLORS[i];
  return `hsl(${Math.round((i * 137.508) % 360)}, 72%, 45%)`;
}

// Pick a readable text color (dark or white) for a given solid background hex,
// so the "in Bewegung" pill stays legible on e.g. yellow trails.
function readableText(hex) {
  if (!hex || hex[0] !== "#") return "#ffffff";
  let h = hex.slice(1);
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L > 0.62 ? "#1c1917" : "#ffffff";
}

// --- Manual device position (fallback when the browser geolocation fails) ---
const MANUAL_POS_COOKIE = "mkl_manual_pos"; // the active manual position (3-day life)
const MANUAL_POS_LAST_COOKIE = "mkl_manual_pos_last"; // remembered values for the next prompt (~eternal)
function readPosCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    if (!m) return null;
    const v = JSON.parse(decodeURIComponent(m[1]));
    return v && Number.isFinite(v.lat) && Number.isFinite(v.lon) ? v : null;
  } catch {
    return null;
  }
}
function writePosCookie(name, value, maxAgeSec) {
  try {
    document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
  } catch {
    /* cookies unavailable */
  }
}
function deletePosCookie(name) {
  try {
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    /* cookies unavailable */
  }
}

// "seit X Min kein Signal" — a Streife whose ACT beacons stopped (dead zone / off).
function keinSignalSeit(sec) {
  const m = Math.floor(Math.max(0, sec) / 60);
  return m < 1 ? "kein Signal" : `seit ${m} Min kein Signal`;
}

// Alarm / last-message model (chip lines below the object name):
//  - active SOS (<10min, unacked) -> red siren, "Alarm um HH:mm" + "vor X Minuten"
//  - old alarm (10min–1h, unacked) -> chip shows only the name; a click reveals "Alarm um HH:mm"
//  - >1h unacked                   -> falls back to last-message display
//  - ACK (<15min)                  -> green check, "Alarm beendet" + "vor X Minuten"
//  - otherwise (trackers + idle)   -> "Meldung um HH:mm" + "vor X Minuten" (from the last message).
//    Trackers whose last message is older than 10 minutes are greyed out (stale) and lose their trail.
function deriveAlarm(item, nowSec, signalTtl = SIGNAL_TTL) {
  const { sosEpoch, ackEpoch, latestEpoch, sensorType } = item;
  const lastTime = latestEpoch != null ? clockOf(latestEpoch) : "";
  const msgAge = latestEpoch != null ? nowSec - latestEpoch : null;
  const isTracker = sensorType === "tracker";
  const stationary = !!item.stationary;
  const noSignal = isTracker && msgAge != null && msgAge > signalTtl;
  // Trackers drop the "Meldung um HH:mm" clock: only "vor X Minuten" (moving)
  // or "stationär seit X Minuten" (moved < 50 m between packets). When ACT beacons
  // stopped for a while we surface a subtle "kein Signal" (possible Funkloch).
  let noneLine1;
  let noneLine2;
  if (isTracker) {
    // Status is shown as an icon on the chip (stationary dot / moving arrow /
    // no-signal). Text lines stay empty except an elapsed-time line that only
    // appears once at least a minute has passed (no "aktualisiert: jetzt").
    noneLine1 = "";
    if (noSignal) {
      noneLine2 = keinSignalSeit(msgAge);
    } else if (item.latitude == null || item.longitude == null) {
      // Sends packets but no GPS fix — mark it as "ohne Position", not stationary.
      noneLine2 = "ohne Position";
    } else if (stationary) {
      // "stationär seit …" reflects how long it has stood still (from the
      // backend anchor), NOT the time since the last packet.
      const statAge = item.stationarySince != null ? nowSec - item.stationarySince : null;
      noneLine2 = statAge != null ? `stationär: ${seitDauer(statAge)}` : "stationär";
    } else if (msgAge != null && msgAge >= 60) {
      noneLine2 = vorMinuten(msgAge);
    } else {
      noneLine2 = "";
    }
  } else {
    // Alarm points: only SOS messages count — never derive a "Meldung um.."
    // line from ACT/beacon packets. SOS states (active/old/ack) show the text.
    noneLine1 = "";
    noneLine2 = "";
  }
  const none = {
    alarmState: "none",
    line1: noneLine1,
    line2: noneLine2,
    stale: isTracker && msgAge != null && msgAge > STALE_TTL,
    stationary,
    noSignal,
    clickLine: "",
    lastTime,
  };
  if (sosEpoch != null) {
    const acked = ackEpoch != null && ackEpoch >= sosEpoch;
    if (acked) {
      if (nowSec - ackEpoch < ACK_TTL) {
        return {
          alarmState: "ack",
          line1: "Alarm beendet",
          line2: vorMinuten(nowSec - ackEpoch),
          stale: false,
          clickLine: "",
          lastTime,
        };
      }
      return none;
    }
    const age = nowSec - sosEpoch;
    if (age < ALARM_RED_TTL) {
      return {
        alarmState: "active",
        line1: `Alarm um ${clockHM(sosEpoch)}`,
        line2: vorMinuten(age),
        stale: false,
        clickLine: "",
        lastTime,
      };
    }
    if (age < ALARM_TTL) {
      // old alarm: name only on the chip, alarm time revealed on click
      return {
        alarmState: "oldalarm",
        line1: "",
        line2: "",
        stale: false,
        clickLine: `Alarm um ${clockHM(sosEpoch)}`,
        lastTime,
      };
    }
    return none;
  }
  return none;
}

const FILTERS = [
  { key: "alarm", label: "Alarmpunkte", Icon: Siren },
  { key: "tracker", label: "Streifen", Icon: Navigation },
];

// The mutually-exclusive Streifen categories used by the filter checkboxes.
const TRACKER_CATS = [
  { cat: "stationary", label: "stationär" },
  { cat: "moving", label: "Bewegung" },
  { cat: "nosignal", label: "kein Signal" },
  { cat: "nopos", label: "ohne Position" },
];

// A Streife falls into exactly one category. A lost signal takes precedence over
// everything; a Streife that still sends but delivers no coordinates is "ohne
// Position" (not "stationär"); otherwise stationary vs. moving.
function trackerCategory(d) {
  if (d.noSignal) return "nosignal";
  if (d.latitude == null || d.longitude == null) return "nopos";
  if (d.stationary) return "stationary";
  return "moving";
}

// Sidebar list filter chips.
const SIDEBAR_FILTERS = [
  { key: "tracker", label: "Streifen" },
  { key: "alarm", label: "Alarmpunkte" },
  { key: "noloc", label: "Ohne Standort" },
  { key: "offline", label: "Ohne Verbindung" },
  { key: "msgerror", label: "Nicht geladen" },
  { key: "lowbatt", label: "Schwacher Akku" },
];

// Copy helper with a fallback for contexts where the async Clipboard API is
// blocked (non-secure origins, some in-app/mobile webviews).
async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function statusOf(item) {
  const text = [item.line1, item.line2].filter(Boolean).join(" · ");
  if (item.alarmState === "active") return { text, cls: "text-red-600" };
  if (item.alarmState === "ack") return { text, cls: "text-emerald-600" };
  if (item.alarmState === "oldalarm")
    return { text: item.clickLine || "", cls: "text-slate-500" };
  if (text) return { text, cls: item.stale ? "text-slate-400" : "text-slate-500" };
  return null;
}

function ObjectRow({ item, onSetLocation, onFocusObject, onFocusLastKnown }) {
  const status = statusOf(item);
  const hasLocation = item.latitude != null && item.longitude != null;
  const hasLast = !!(item.lastKnown && item.lastKnown.lat != null && item.lastKnown.lon != null);
  const clickable = hasLocation || hasLast;
  const handleFocus = () => {
    if (hasLocation) onFocusObject && onFocusObject(item);
    else if (hasLast) onFocusLastKnown && onFocusLastKnown(item);
  };
  const lastKnownInfo =
    !hasLocation && hasLast
      ? `zuletzt ${clockHM(item.lastKnown.epoch)} · ${
          vorMinuten(Math.max(0, Date.now() / 1000 - item.lastKnown.epoch)) || "gerade eben"
        }`
      : null;
  const editObject = () => {
    if (!item.iotId) return;
    window.open(`${SETUP_URL}/?id=${encodeURIComponent(item.iotId)}`, "_blank", "noopener");
  };
  const copyCoords = async () => {
    if (!hasLocation) return;
    const link = `https://www.google.com/maps?q=${item.latitude.toFixed(6)},${item.longitude.toFixed(6)}`;
    const ok = await copyText(link);
    if (ok) toast.success("Google-Maps-Link kopiert");
    else toast.error("Kopieren fehlgeschlagen");
  };
  const copyEui = async () => {
    if (!item.devEui) return;
    const ok = await copyText(item.devEui);
    if (ok) toast.success("DevEUI kopiert");
    else toast.error("Kopieren fehlgeschlagen");
  };
  return (
    <div
      className="group flex items-center gap-2 px-4 py-2.5 transition-colors hover:bg-slate-50"
      data-testid={`home-obj-row-${item.permid}`}
    >
      <button
        type="button"
        onClick={handleFocus}
        disabled={!clickable}
        data-testid={`home-obj-focus-${item.permid}`}
        title={
          hasLocation
            ? "Auf der Karte anzeigen"
            : hasLast
            ? "Zuletzt bekannte Position anzeigen"
            : "Kein Standort"
        }
        className="min-w-0 flex-1 text-left enabled:cursor-pointer disabled:cursor-default"
      >
        <div className="flex items-center gap-1.5">
          {item.sensorType === "tracker" && item.alarmState === "none" && (
            item.noSignal ? (
              <WifiOff size={13} className="shrink-0 text-amber-500" />
            ) : item.latitude == null || item.longitude == null ? (
              <MapPinOff size={13} className="shrink-0 text-slate-400" />
            ) : item.stationary ? (
              <Circle size={11} className="shrink-0 fill-green-500 text-green-500" />
            ) : (
              <Navigation size={13} className="shrink-0 -rotate-45 fill-blue-600 text-blue-600" />
            )
          )}
          <span className="truncate text-sm font-semibold text-slate-800">
            {item.title || "Ohne Titel"}
          </span>
        </div>
        {status ? (
          <div className={`text-[11px] font-semibold ${status.cls}`}>{status.text}</div>
        ) : item.msgError ? (
          <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-600">
            <AlertTriangle size={11} /> Nicht geladen
          </div>
        ) : item.latitude == null ? (
          <div className="text-[11px] font-medium text-slate-400">Ohne Standort</div>
        ) : (
          <div
            className={`text-[11px] font-semibold ${
              item.offline ? "text-amber-600" : "text-slate-400"
            }`}
          >
            {checkText(item.checkAge)}
          </div>
        )}
        {lastKnownInfo && (
          <div
            className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-slate-500"
            data-testid={`home-obj-lastknown-${item.permid}`}
          >
            <MapPin size={11} className="shrink-0 text-slate-400" /> {lastKnownInfo}
          </div>
        )}
        {item.battery != null && (() => {
          const b = item.battery;
          const high = b > 75; // full & green: just the icon, no number
          const Icon = high ? BatteryFull : b <= 20 ? BatteryWarning : b <= 40 ? BatteryLow : BatteryMedium;
          const cls = high
            ? "text-green-600"
            : b <= 20
            ? "text-red-600"
            : b <= 40
            ? "text-amber-600"
            : "text-slate-500";
          return (
            <div
              className={`mt-0.5 flex items-center gap-1 text-[11px] font-semibold ${cls}`}
              data-testid={`home-obj-battery-${item.permid}`}
              title={high ? `Akku ${b}%` : undefined}
            >
              <Icon size={14} className="shrink-0" />
              {!high && <span>{batteryText(b, item.batteryHours)}</span>}
            </div>
          );
        })()}
      </button>
      {clickable && (
        <button
          type="button"
          onClick={handleFocus}
          data-testid={`home-obj-locate-${item.permid}`}
          aria-label={hasLocation ? "Auf der Karte anzeigen" : "Zuletzt bekannte Position anzeigen"}
          title={hasLocation ? "Auf der Karte anzeigen" : "Zuletzt bekannte Position anzeigen"}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 opacity-0 transition-all hover:bg-slate-100 hover:text-slate-700 focus-visible:opacity-100 group-hover:opacity-100"
        >
          {hasLocation ? <LocateFixed size={16} /> : <MapPin size={16} />}
        </button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Optionen"
            data-testid={`home-obj-menu-${item.permid}`}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
          >
            <MoreVertical size={17} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[900] w-56">
          {item.devEui && (
            <>
              <DropdownMenuLabel className="pb-0 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                DevEUI
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={copyEui}
                data-testid={`home-obj-eui-${item.permid}`}
                className="gap-2 font-mono text-xs tracking-tight"
              >
                <Radio size={15} className="shrink-0" /> {item.devEui}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem
            disabled={!item.iotId}
            onClick={editObject}
            data-testid={`home-obj-edit-${item.permid}`}
            className="gap-2"
          >
            <Pencil size={15} /> Objekt bearbeiten
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onSetLocation(item)}
            data-testid={`home-obj-setloc-${item.permid}`}
            className="gap-2"
          >
            <MapPin size={15} /> Standort setzen
          </DropdownMenuItem>
          {hasLocation && (
            <DropdownMenuItem
              onClick={copyCoords}
              data-testid={`home-obj-copycoords-${item.permid}`}
              className="gap-2 tabular-nums"
            >
              <Copy size={15} /> {item.latitude.toFixed(5)}, {item.longitude.toFixed(5)}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function HomeView() {
  const [data, setData] = useState({ rootTitle: null, items: [], loading: true });
  const [viewerCount, setViewerCount] = useState(0);
  const [viewerId] = useState(() => {
    try {
      let id = sessionStorage.getItem("viewer_id");
      if (!id) {
        id =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem("viewer_id", id);
      }
      return id;
    } catch {
      return `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    }
  });
  const [filter, setFilter] = useState(null); // null | 'alarm' | 'tracker'
  const [tick, setTick] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("kiosk") === "1";
    } catch {
      return false;
    }
  }); // "Karte pur": hide side/bottom command UI
  const [userPos, setUserPos] = useState(null); // { lat, lon, accuracy }
  const [setLocTarget, setSetLocTarget] = useState(null); // { permid, title }
  const [savingLoc, setSavingLoc] = useState(false);
  // set-location dialog coordinate state (auto GPS or hand-typed)
  const [locPos, setLocPos] = useState(null); // { lat, lon, accuracy }
  const [locManual, setLocManual] = useState(false);
  const [editCoords, setEditCoords] = useState(false);
  const [latStr, setLatStr] = useState("");
  const [lonStr, setLonStr] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [conn, setConn] = useState({ online: true, lastOk: null, msgErrors: 0 });
  const [layersOpen, setLayersOpen] = useState(false);
  const [baseLayer, setBaseLayerState] = useState(() => {
    try {
      return localStorage.getItem("homeBaseLayer") || "standard";
    } catch {
      return "standard";
    }
  });
  const baseLayerRef = useRef(baseLayer); // latest intended value (guards poll sync)
  const lastBaseChangeRef = useRef(0); // ts of the last local change
  const setBaseLayer = useCallback((key) => {
    baseLayerRef.current = key;
    lastBaseChangeRef.current = Date.now();
    setBaseLayerState(key);
    try {
      localStorage.setItem("homeBaseLayer", key);
    } catch {
      /* ignore */
    }
    api.updateSettings({ baseLayer: key }).catch(() => {}); // shared for all viewers
  }, []);
  // Apply a base map coming from the server (initial load + live poll), unless
  // the user just changed it locally.
  const syncBaseLayer = useCallback((key) => {
    if (!key || key === baseLayerRef.current) return;
    if (Date.now() - lastBaseChangeRef.current < 6000) return;
    baseLayerRef.current = key;
    setBaseLayerState(key);
    try {
      localStorage.setItem("homeBaseLayer", key);
    } catch {
      /* ignore */
    }
  }, []);
  const [focusReq, setFocusReq] = useState(null); // { permid, lat, lon, n }
  const [soundOn, setSoundOn] = useState(() => {
    try {
      return localStorage.getItem("sos_sound") === "1";
    } catch {
      return false;
    }
  });
  const [alarmParam, setAlarmParam] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("alarm");
    } catch {
      return null;
    }
  });
  const [radiusM, setRadiusM] = useState(50); // stationary-detection radius (m)
  const [windowN, setWindowN] = useState(3); // stationary-detection window (packets)
  const [signalMin, setSignalMin] = useState(5); // "kein Signal" threshold (minutes)
  const [offlineMin, setOfflineMin] = useState(70); // "Ohne Verbindung": last check older than this (minutes)
  const [rotateOverlapS, setRotateOverlapS] = useState(3); // foreground rotation of overlapping chips (s; 0=off)
  const [trailPoints, setTrailPoints] = useState(10); // movement-trail length (number of recent positions)
  const [moveBannerIdx, setMoveBannerIdx] = useState(0); // which moving Streife the top banner shows
  const [mapTick, setMapTick] = useState(0); // bumps on map pan/zoom to redraw the banner line
  const [trackerCats, setTrackerCats] = useState({ stationary: true, moving: true, nosignal: true, nopos: true });
  const [sidebarFilter, setSidebarFilter] = useState(null); // null | 'tracker' | 'alarm' | 'noloc' | 'offline'
  const [deployInfo, setDeployInfo] = useState(null); // { startedAt } — last deployment/backend start
  const mapApiRef = useRef(null);
  const ov = useHomeOverlay(mapApiRef);
  const audioCtxRef = useRef(null);
  const activeSosRef = useRef(null);
  const alarmFitRef = useRef({ obj: false, user: false });
  const clockOffset = useRef(0);
  const fetchingRef = useRef(false); // prevents overlapping retry loops from the poll
  const locOverridesRef = useRef(new Map()); // permid -> {lat, lon}, sticky until cache catches up
  const colorAssignRef = useRef(new Map()); // permid -> color slot; persists through signal loss

  // Stationary-detection settings: load once, persist on change (server-side).
  useEffect(() => {
    let ok = true;
    api
      .getSettings()
      .then(({ data }) => {
        if (!ok) return;
        if (data?.stationaryRadiusM != null) setRadiusM(Math.round(data.stationaryRadiusM));
        if (data?.stationaryWindow != null) setWindowN(Math.round(data.stationaryWindow));
        if (data?.signalTtlS != null) setSignalMin(Math.max(1, Math.round(data.signalTtlS / 60)));
        if (data?.offlineTtlS != null) setOfflineMin(Math.max(5, Math.round(data.offlineTtlS / 60)));
        if (data?.rotateOverlapS != null) setRotateOverlapS(Math.max(0, Math.round(data.rotateOverlapS)));
        if (data?.trailPoints != null) setTrailPoints(Math.max(2, Math.round(data.trailPoints)));
        if (data?.baseLayer) syncBaseLayer(data.baseLayer);
      })
      .catch(() => {});
    return () => {
      ok = false;
    };
  }, [syncBaseLayer]);

  // Deployment info (backend start time) for the settings footer.
  useEffect(() => {
    api
      .getVersion()
      .then(({ data }) => setDeployInfo(data))
      .catch(() => {});
  }, []);

  const saveRadius = useCallback((m) => {
    api.updateSettings({ stationaryRadiusM: m }).catch(() => {});
  }, []);

  const saveWindow = useCallback((n) => {
    api.updateSettings({ stationaryWindow: n }).catch(() => {});
  }, []);

  const saveSignal = useCallback((min) => {
    api.updateSettings({ signalTtlS: min * 60 }).catch(() => {});
  }, []);

  const saveOffline = useCallback((min) => {
    api.updateSettings({ offlineTtlS: min * 60 }).catch(() => {});
  }, []);

  const saveRotate = useCallback((s) => {
    api.updateSettings({ rotateOverlapS: s }).catch(() => {});
  }, []);
  const saveTrail = useCallback((n) => {
    api.updateSettings({ trailPoints: n }).catch(() => {});
  }, []);

  const applyOverrides = useCallback((list) => {
    const ov = locOverridesRef.current;
    if (ov.size === 0) return list;
    return list.map((i) => {
      const o = ov.get(i.permid);
      if (!o) return i;
      if (
        i.latitude != null &&
        Math.abs(i.latitude - o.lat) < 1e-5 &&
        Math.abs(i.longitude - o.lon) < 1e-5
      ) {
        ov.delete(i.permid); // backend now reports the new position
        return i;
      }
      return { ...i, latitude: o.lat, longitude: o.lon };
    });
  }, []);

  const fetchHome = useCallback(async () => {
    if (fetchingRef.current) return; // a retry loop is already running
    fetchingRef.current = true;
    const MAX_RETRIES = 3; // 1 initial attempt + 3 retries before flagging offline
    const RETRY_DELAY = 2000;
    try {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const { data: d } = await api.getHome({ viewer: viewerId });
          if (d.serverTime) {
            clockOffset.current = new Date(d.serverTime).getTime() / 1000 - Date.now() / 1000;
          }
          setData({ ...d, items: applyOverrides(d.items || []) });
          if (d.viewerCount != null) setViewerCount(d.viewerCount);
          syncBaseLayer(d.baseLayer);
          if (d.loading) {
            setConn((c) => ({ ...c, online: true }));
          } else {
            setConn({ online: true, lastOk: Date.now(), msgErrors: d.msgErrors || 0 });
          }
          return; // success — stop retrying
        } catch {
          // Communication error (bad connection / timeout): retry a few times with a
          // short delay before giving up. Keep previous data throughout.
          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY));
            continue;
          }
          setConn((c) => ({ ...c, online: false }));
        }
      }
    } finally {
      fetchingRef.current = false;
    }
  }, [applyOverrides, viewerId, syncBaseLayer]);

  // Manual "Jetzt aktualisieren": forces a blocking live recompute on the
  // backend (bypasses the background snapshot) and shows the result.
  const handleManualRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { data: d } = await api.getHome({ refresh: true, viewer: viewerId });
      if (d.serverTime) {
        clockOffset.current = new Date(d.serverTime).getTime() / 1000 - Date.now() / 1000;
      }
      setData({ ...d, items: applyOverrides(d.items || []) });
      if (d.viewerCount != null) setViewerCount(d.viewerCount);
      setConn({ online: true, lastOk: Date.now(), msgErrors: d.msgErrors || 0 });
      toast.success("Aktualisiert");
    } catch {
      setConn((c) => ({ ...c, online: false }));
      toast.error("Aktualisierung fehlgeschlagen");
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, applyOverrides, viewerId]);

  useEffect(() => {
    fetchHome();
    const id = setInterval(fetchHome, POLL_MS);
    return () => clearInterval(id);
  }, [fetchHome]);

  // While offline: re-check the connection every 10 s so the app recovers by
  // itself once the internet is back.
  useEffect(() => {
    if (conn.online) return;
    const id = setInterval(fetchHome, 10000);
    return () => clearInterval(id);
  }, [conn.online, fetchHome]);

  // React instantly to the browser's own online/offline events.
  useEffect(() => {
    const onOnline = () => fetchHome();
    const onOffline = () => setConn((c) => ({ ...c, online: false }));
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [fetchHome]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // "Karte pur": toggle our clean-map UI together with the browser Fullscreen
  // API so mobile browser chrome (address bar, tabs) is hidden too. Requesting
  // fullscreen must happen inside the click gesture; iOS Safari ignores it for
  // non-video elements (there the UI still hides, just not the Safari bars).
  const toggleFocusMode = () => {
    const next = !focusMode;
    setFocusMode(next);
    try {
      const el = document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
      if (next) {
        if (req) {
          const p = req.call(el);
          if (p && p.catch) p.catch(() => {});
        } else {
          // iOS Safari has no element Fullscreen API — the Safari bars can only
          // be hidden by launching from the Home Screen (standalone). Nudge the
          // user there instead of silently doing nothing.
          const standalone =
            window.navigator.standalone === true ||
            (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
          if (!standalone) {
            toast.info(
              "Für randloses Vollbild am iPhone: In Safari auf „Teilen“ → „Zum Home-Bildschirm“ tippen und die App von dort öffnen.",
              { duration: 7000 }
            );
          }
        }
      } else {
        const inFs =
          document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
        const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
        if (inFs && exit) {
          const p = exit.call(document);
          if (p && p.catch) p.catch(() => {});
        }
      }
    } catch {
      /* fullscreen not available — UI still toggles */
    }
  };

  // Keep our clean-map UI in sync when the user leaves fullscreen via the
  // browser (Esc / swipe): bring all menus back automatically.
  useEffect(() => {
    const onFsChange = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
      if (!fs) setFocusMode(false);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  // Kiosk mode (?kiosk=1): boots straight into "Karte pur" and, after a period
  // of no interaction, snaps back to it — ideal for a wall-mounted lage monitor.
  // Optional ?idle=<seconds> overrides the default 60 s idle window.
  const kiosk = useMemo(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("kiosk") !== "1") return null;
      const raw = parseInt(q.get("idle") || "60", 10);
      const secs = Number.isFinite(raw) ? Math.max(10, raw) : 60;
      return { idleMs: secs * 1000 };
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!kiosk) return;
    let t;
    const reset = () => {
      clearTimeout(t);
      t = setTimeout(() => setFocusMode(true), kiosk.idleMs);
    };
    const evts = ["pointerdown", "keydown", "touchstart", "wheel"];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(t);
      evts.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [kiosk]);

  const [manualPosOpen, setManualPosOpen] = useState(false);
  const [manualLat, setManualLat] = useState("");
  const [manualLon, setManualLon] = useState("");
  const gotBrowserPosRef = useRef(false);
  const latestGpsRef = useRef(null); // most recent live GPS fix (used when manual is cleared)
  const manualActiveRef = useRef(!!readPosCookie(MANUAL_POS_COOKIE)); // mirror for the geolocation closure
  const [manualActive, setManualActive] = useState(() => !!readPosCookie(MANUAL_POS_COOKIE));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pickLocation, setPickLocation] = useState(false); // "tap the map to set my location" mode

  // Set (and persist) a manual device position. Takes priority over live GPS
  // until the user deletes it again.
  const setManualPosition = useCallback((lat, lon) => {
    setUserPos({ lat, lon, accuracy: null, manual: true });
    manualActiveRef.current = true;
    setManualActive(true);
    writePosCookie(MANUAL_POS_COOKIE, { lat, lon }, 3 * 24 * 3600); // active: 3 days
    writePosCookie(MANUAL_POS_LAST_COOKIE, { lat, lon }, 3650 * 24 * 3600); // remembered: ~10 years
  }, []);

  // Delete the manual position → revert to live GPS (if we have one), else none.
  const clearManualPos = useCallback(() => {
    deletePosCookie(MANUAL_POS_COOKIE);
    manualActiveRef.current = false;
    setManualActive(false);
    setUserPos(latestGpsRef.current || null);
    toast.success("Manueller Standort gelöscht");
  }, []);

  // Open the manual LAT/LONG entry, pre-filled with the last values the user
  // typed (remembered "forever"), or the currently active manual position.
  const openManualPos = useCallback(() => {
    const last = readPosCookie(MANUAL_POS_LAST_COOKIE) || readPosCookie(MANUAL_POS_COOKIE);
    setManualLat(last ? String(last.lat) : "");
    setManualLon(last ? String(last.lon) : "");
    setManualPosOpen(true);
  }, []);

  const applyManualPos = useCallback(() => {
    const lat = parseFloat(String(manualLat).trim().replace(",", "."));
    const lon = parseFloat(String(manualLon).trim().replace(",", "."));
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      toast.error("Bitte gültige Koordinaten eingeben (Breite −90…90, Länge −180…180).");
      return;
    }
    setManualPosition(lat, lon);
    setManualPosOpen(false);
    toast.success("Standort manuell gesetzt");
  }, [manualLat, manualLon, setManualPosition]);

  // Enter "tap the map" mode: close the settings popover and wait for one click.
  const startPickLocation = useCallback(() => {
    setSettingsOpen(false);
    setManualPosOpen(false);
    setPickLocation(true);
  }, []);

  // A tap on the map (pick mode) sets the manual position and exits the mode.
  const handleMapPick = useCallback(
    (lat, lon) => {
      setManualPosition(lat, lon);
      setPickLocation(false);
      toast.success("Standort auf der Karte gesetzt");
    },
    [setManualPosition]
  );

  // Live geolocation (blue dot like Google Maps). If the browser can't provide a
  // position, fall back to a saved manual one (3-day cookie) or, if none, prompt
  // the user for LAT/LONG (optional — dismissible). A manually set position always
  // takes priority over live GPS until the user deletes it.
  useEffect(() => {
    const savedActive = readPosCookie(MANUAL_POS_COOKIE);
    if (savedActive) {
      manualActiveRef.current = true;
      setUserPos({ lat: savedActive.lat, lon: savedActive.lon, accuracy: null, manual: true });
    }
    if (!navigator.geolocation) {
      if (!savedActive) openManualPos();
      return;
    }
    const onPos = (p) => {
      gotBrowserPosRef.current = true;
      const gps = { lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy };
      latestGpsRef.current = gps;
      if (manualActiveRef.current) return; // manual position wins
      setUserPos(gps);
    };
    const onErr = () => {
      if (gotBrowserPosRef.current || readPosCookie(MANUAL_POS_COOKIE)) return;
      openManualPos();
    };
    navigator.geolocation.getCurrentPosition(onPos, onErr, {
      enableHighAccuracy: true,
      timeout: 10000,
    });
    const id = navigator.geolocation.watchPosition(onPos, () => {}, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 15000,
    });
    return () => navigator.geolocation.clearWatch(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = data.rootTitle
      ? `${data.rootTitle} · Live-Karte`
      : "Makrolog · Live-Karte";
  }, [data.rootTitle]);

  const nowSec = Date.now() / 1000 + clockOffset.current;
  const items = data.items || [];

  // Live clock (top-right card) — re-rendered every second by the tick above.
  const clockNow = new Date();
  const clockTime = clockNow.toLocaleTimeString("de-DE", {
    timeZone: "Europe/Berlin",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const clockParts = clockTime.split(":");
  const clockHm = `${clockParts[0]}:${clockParts[1]}`;
  const clockSec = clockParts[2] || "";
  const clockDate = clockNow.toLocaleDateString("de-DE", {
    timeZone: "Europe/Berlin",
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const lastOkStr = conn.lastOk
    ? new Date(conn.lastOk).toLocaleTimeString("de-DE", {
        timeZone: "Europe/Berlin",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : null;

  const counts = useMemo(() => {
    const c = { alarm: 0, tracker: 0, stationary: 0, moving: 0, alarmNoLoc: 0, trackerNoLoc: 0 };
    items.forEach((i) => {
      const noLoc = i.latitude == null || i.longitude == null;
      if (i.sensorType === "alarm") {
        c.alarm += 1;
        if (noLoc) c.alarmNoLoc += 1;
      } else if (i.sensorType === "tracker") {
        c.tracker += 1;
        if (noLoc) c.trackerNoLoc += 1;
        if (i.stationary) c.stationary += 1;
        else if (i.heading != null) c.moving += 1;
      }
    });
    return c;
  }, [items]);

  // Most recent SOS across all objects, kept for 30 min after it fired
  // (independent of the blinking/timeout window on the chip).
  const lastAlarm = useMemo(() => {
    let best = null;
    items.forEach((i) => {
      if (i.sosEpoch != null && nowSec - i.sosEpoch < 1800) {
        if (!best || i.sosEpoch > best.sosEpoch) best = i;
      }
    });
    return best;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, tick]);

  // All objects with alarm state (drives the sidebar list). Also derives the
  // "check" age (time since last message) and the "Ohne Verbindung" flag.
  const decorated = useMemo(() => {
    return items.map((i) => {
      const d = deriveAlarm(i, nowSec, signalMin * 60);
      const checkAge = i.latestEpoch != null ? nowSec - i.latestEpoch : null;
      const offline = checkAge != null && checkAge > offlineMin * 60;
      return { ...i, ...d, checkAge, offline };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, tick, signalMin, offlineMin]);

  // Deep-link target (?alarm=<title>): show only this rescue point + own location.
  const alarmTarget = useMemo(() => {
    if (!alarmParam) return null;
    const q = alarmParam.trim().toLowerCase();
    return decorated.find((d) => (d.title || "").trim().toLowerCase() === q) || null;
  }, [alarmParam, decorated]);

  // Per-category Streifen counts for the filter checkboxes (uses derived state
  // so "kein Signal" reflects the live threshold).
  const catCounts = useMemo(() => {
    const c = { stationary: 0, moving: 0, nosignal: 0, nopos: 0 };
    decorated.forEach((d) => {
      if (d.sensorType !== "tracker") return;
      c[trackerCategory(d)] += 1;
    });
    return c;
  }, [decorated]);

  // Stable, distinct color per moving Streife. A Streife that was moving keeps
  // its color while it has "kein Signal" (the color is only released once it is
  // no longer moving and its signal is present again, or the object disappears).
  const movingColorMap = useMemo(() => {
    const assign = colorAssignRef.current;
    const wanted = new Set();
    decorated.forEach((d) => {
      if (d.sensorType !== "tracker") return;
      const activeMoving =
        d.alarmState === "none" && !d.stationary && !d.noSignal;
      const lostButColored = d.noSignal && assign.has(d.permid);
      if (activeMoving || lostButColored) wanted.add(d.permid);
    });
    Array.from(assign.keys()).forEach((permid) => {
      if (!wanted.has(permid)) assign.delete(permid);
    });
    const used = new Set(assign.values());
    Array.from(wanted)
      .filter((p) => !assign.has(p))
      .sort()
      .forEach((permid) => {
        let idx = 0;
        while (used.has(idx)) idx += 1;
        used.add(idx);
        assign.set(permid, idx);
      });
    const m = {};
    wanted.forEach((permid) => {
      m[permid] = moveColorAt(assign.get(permid));
    });
    return m;
  }, [decorated]);

  // Objects for the map: alarm deep-link shows only the target; otherwise the
  // alarm/tracker filter + the per-category checkboxes apply (map skips no-GPS
  // ones). Moving Streifen carry their assigned color.
  const computed = useMemo(() => {
    if (alarmParam) {
      const base = alarmTarget ? [alarmTarget] : [];
      return base.map((i) => ({ ...i, moveColor: movingColorMap[i.permid] || null }));
    }
    let base = filter ? decorated.filter((i) => i.sensorType === filter) : decorated;
    base = base.filter(
      (i) => i.sensorType !== "tracker" || trackerCats[trackerCategory(i)]
    );
    return base.map((i) => {
      const c = { ...i, moveColor: movingColorMap[i.permid] || null };
      // "ohne Position" trackers stay on the map as a pale ghost pin at their
      // last known spot, so the control room sees where they were last seen.
      if (
        i.sensorType === "tracker" &&
        (i.latitude == null || i.longitude == null) &&
        i.lastKnown &&
        i.lastKnown.lat != null
      ) {
        const rel = vorMinuten(Math.max(0, nowSec - i.lastKnown.epoch)) || "gerade eben";
        return {
          ...c,
          latitude: i.lastKnown.lat,
          longitude: i.lastKnown.lon,
          ghost: true,
          heading: null,
          stationary: false,
          line1: "",
          line2: `zuletzt ${clockHM(i.lastKnown.epoch)} · ${rel}`,
        };
      }
      return c;
    });
  }, [decorated, filter, alarmParam, alarmTarget, movingColorMap, trackerCats, nowSec]);

  // Sidebar list respects the sidebar filter chips.
  const sidebarItems = useMemo(() => {
    if (!sidebarFilter) return decorated;
    return decorated.filter((d) => {
      if (sidebarFilter === "tracker") return d.sensorType === "tracker";
      if (sidebarFilter === "alarm") return d.sensorType === "alarm";
      if (sidebarFilter === "noloc") return d.latitude == null || d.longitude == null;
      if (sidebarFilter === "offline") return d.offline;
      if (sidebarFilter === "msgerror") return !!d.msgError;
      if (sidebarFilter === "lowbatt") return d.battery != null && d.battery < 20;
      return true;
    });
  }, [decorated, sidebarFilter]);

  // Straight guide line + distance from the user to the alarm target.
  const guideLine = useMemo(() => {
    if (!alarmParam || !alarmTarget || alarmTarget.latitude == null || !userPos) return null;
    return {
      from: [userPos.lat, userPos.lon],
      to: [alarmTarget.latitude, alarmTarget.longitude],
    };
  }, [alarmParam, alarmTarget, userPos]);

  const alarmDistance = useMemo(() => {
    if (!guideLine) return null;
    const [la1, lo1] = guideLine.from;
    const [la2, lo2] = guideLine.to;
    const R = 6371000;
    const dLat = ((la2 - la1) * Math.PI) / 180;
    const dLon = ((lo2 - lo1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((la1 * Math.PI) / 180) * Math.cos((la2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    const m = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return m >= 1000 ? `${(m / 1000).toFixed(1).replace(".", ",")} km` : `${Math.round(m)} m`;
  }, [guideLine]);

  const markerSig = useMemo(
    () =>
      computed
        .map(
          (c) =>
            `${c.permid}:${c.latitude?.toFixed(5)}:${c.longitude?.toFixed(5)}:${c.alarmState}:${c.heading == null ? "" : Math.round(c.heading)}:${c.moveColor || ""}:${c.stationary ? "S" : ""}:${c.ghost ? "G" : ""}:${c.battery != null && c.battery < 20 ? c.battery : ""}`
        )
        .join("|"),
    [computed]
  );
  const timeSig = useMemo(
    () => computed.map((c) => `${c.line1}~${c.line2}~${c.lastTime}~${c.stale}~${c.noSignal}`).join("|"),
    [computed]
  );
  const trailSig = useMemo(
    () =>
      computed
        .map((c) => `${c.permid}:${c.stale}:${c.stationary}:${c.moveColor || ""}:${(c.trail || []).length}:${(c.trail || [])[0]?.join(",")}`)
        .join("|"),
    [computed]
  );
  const fitSig = useMemo(() => computed.map((c) => c.permid).sort().join(","), [computed]);

  // Streifen that are currently moving → shown in the top "in Bewegung" banner.
  // A Streife is only shown as "in Bewegung" while its last packet is fresh.
  // After > 2 min 20 s (140 s) without a new packet we hide the banner — the
  // extra slack over 2 min tolerates packets that occasionally arrive late.
  const movingList = useMemo(
    () =>
      computed.filter(
        (i) =>
          i.sensorType === "tracker" &&
          !i.stationary &&
          i.heading != null &&
          i.latitude != null &&
          i.longitude != null &&
          i.alarmState === "none" &&
          i.latestEpoch != null &&
          Date.now() / 1000 - i.latestEpoch <= 140
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [computed, tick]
  );
  const currentMoving = movingList.length ? movingList[moveBannerIdx % movingList.length] : null;
  const movingColor = (currentMoving && currentMoving.moveColor) || "#f59e0b";
  const movingText = readableText(movingColor);
  const movePt = useMemo(() => {
    if (!currentMoving || !mapApiRef.current?.project) return null;
    return mapApiRef.current.project(currentMoving.latitude, currentMoving.longitude);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMoving, mapTick]);

  // Cycle the banner through all moving Streifen every 3 s.
  useEffect(() => {
    setMoveBannerIdx(0);
    if (movingList.length <= 1) return;
    const id = setInterval(() => setMoveBannerIdx((i) => i + 1), 3000);
    return () => clearInterval(id);
  }, [movingList.length]);

  // Redraw the banner→Streife line whenever the map pans/zooms.
  useEffect(() => {
    const api = mapApiRef.current;
    if (!api?.onViewChange) return;
    api.onViewChange(() => setMapTick((t) => t + 1));
    return () => {
      if (mapApiRef.current?.onViewChange) mapApiRef.current.onViewChange(null);
    };
  }, []);

  const toggleFilter = (key) => setFilter((prev) => (prev === key ? null : key));
  const toggleCat = useCallback(
    (cat) => setTrackerCats((c) => ({ ...c, [cat]: !c[cat] })),
    []
  );
  const toggleSidebarFilter = useCallback(
    (key) => setSidebarFilter((prev) => (prev === key ? null : key)),
    []
  );

  // List click → jump the map to that object (and flash it).
  const onFocusObject = useCallback((item) => {
    if (item.latitude == null || item.longitude == null) return;
    setSidebarOpen(false);
    setFocusReq({ permid: item.permid, lat: item.latitude, lon: item.longitude, n: Date.now() });
  }, []);

  // "Ohne Position" click → jump to the last known spot (ghost pin) and flash it.
  const onFocusLastKnown = useCallback((item) => {
    const lk = item.lastKnown;
    if (!lk || lk.lat == null || lk.lon == null) return;
    setSidebarOpen(false);
    setFocusReq({ permid: item.permid, lat: lk.lat, lon: lk.lon, n: Date.now() });
  }, []);

  // --- SOS alarm sound (bell top-right) ---
  const ensureAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioCtxRef.current = new AC();
    }
    const ctx = audioCtxRef.current;
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    return ctx;
  }, []);

  const playBeep = useCallback(() => {
    const ctx = ensureAudioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    [0, 0.28].forEach((t, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = i === 0 ? 880 : 1180;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.35, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.24);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.26);
    });
  }, [ensureAudioCtx]);

  // Play the exact SOS alarm 3× in a row so it can be tested without a real alarm.
  const playTestTone = useCallback(() => {
    ensureAudioCtx();
    playBeep();
    setTimeout(() => playBeep(), 700);
    setTimeout(() => playBeep(), 1400);
    if (!soundOn) {
      toast.info("Alarmton ist ausgeschaltet – der Testklang wird trotzdem abgespielt.");
    }
  }, [ensureAudioCtx, playBeep, soundOn]);

  const toggleSound = useCallback(() => {
    setSoundOn((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("sos_sound", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      if (next) {
        ensureAudioCtx();
        setTimeout(() => playBeep(), 60); // confirmation blip on the user gesture
      }
      return next;
    });
  }, [ensureAudioCtx, playBeep]);

  // Ring once whenever an object newly enters the active-SOS state.
  useEffect(() => {
    const cur = new Set(
      decorated.filter((d) => d.alarmState === "active").map((d) => d.permid)
    );
    const prev = activeSosRef.current;
    activeSosRef.current = cur;
    if (prev === null) return; // don't ring for alarms already active on first load
    let isNew = false;
    cur.forEach((p) => {
      if (!prev.has(p)) isNew = true;
    });
    if (isNew && soundOn) playBeep();
  }, [decorated, soundOn, playBeep]);

  // Alarm deep-link: fit the map to the target + own location (once each).
  useEffect(() => {
    if (!alarmParam) {
      alarmFitRef.current = { obj: false, user: false };
      return;
    }
    if (!alarmTarget || alarmTarget.latitude == null) return;
    const hasUser = !!userPos;
    const st = alarmFitRef.current;
    if (!st.obj || (hasUser && !st.user)) {
      const pts = [[alarmTarget.latitude, alarmTarget.longitude]];
      if (hasUser) pts.push([userPos.lat, userPos.lon]);
      mapApiRef.current?.fitToPoints(pts);
      alarmFitRef.current = { obj: true, user: hasUser };
    }
  }, [alarmParam, alarmTarget, userPos]);

  const exitAlarm = useCallback(() => {
    setAlarmParam(null);
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("alarm");
      window.history.replaceState({}, "", u.pathname + u.search);
    } catch {
      /* ignore */
    }
  }, []);

  const openSetLoc = useCallback(
    (item) => {
      if (!userPos) {
        toast.error("Kein Standort verfügbar – bitte Standortfreigabe aktivieren.");
        return;
      }
      setLocManual(false);
      setEditCoords(false);
      setLocPos({ lat: userPos.lat, lon: userPos.lon, accuracy: userPos.accuracy });
      // defer so the closing dropdown doesn't clash with the opening dialog
      setTimeout(() => setSetLocTarget({ permid: item.permid, title: item.title }), 10);
    },
    [userPos]
  );

  // Keep the dialog coordinates on the live GPS fix until the user edits by hand.
  useEffect(() => {
    if (!setLocTarget || locManual || !userPos) return;
    setLocPos({ lat: userPos.lat, lon: userPos.lon, accuracy: userPos.accuracy });
  }, [setLocTarget, userPos, locManual]);

  const startEditCoords = useCallback(() => {
    if (locPos) {
      setLatStr(String(locPos.lat));
      setLonStr(String(locPos.lon));
    }
    setEditCoords(true);
  }, [locPos]);

  const resetToGps = useCallback(() => {
    setLocManual(false);
    setEditCoords(false);
    if (userPos) setLocPos({ lat: userPos.lat, lon: userPos.lon, accuracy: userPos.accuracy });
  }, [userPos]);

  const onCoordChange = useCallback((which, v) => {
    if (which === "lat") setLatStr(v);
    else setLonStr(v);
    setLocManual(true);
    const n = parseFloat(v);
    if (Number.isFinite(n)) {
      setLocPos((p) => ({
        lat: which === "lat" ? n : p?.lat ?? 0,
        lon: which === "lon" ? n : p?.lon ?? 0,
        accuracy: null,
      }));
    }
  }, []);

  const confirmSetLocation = useCallback(async () => {
    if (!setLocTarget || !locPos) return;
    setSavingLoc(true);
    try {
      await api.setHomeLocation(setLocTarget.permid, locPos.lat, locPos.lon);
      // Reload just this object's fresh coordinates so the change is visible
      // immediately (the /api/home cache refreshes slower in the background).
      try {
        const { data: obj } = await api.getObject(setLocTarget.permid);
        if (obj && obj.latitude != null) {
          locOverridesRef.current.set(setLocTarget.permid, {
            lat: obj.latitude,
            lon: obj.longitude,
          });
          setData((prev) => ({
            ...prev,
            items: (prev.items || []).map((i) =>
              i.permid === setLocTarget.permid
                ? { ...i, latitude: obj.latitude, longitude: obj.longitude }
                : i
            ),
          }));
        }
      } catch {
        /* home poll will catch up */
      }
      toast.success("Standort gesetzt");
      setSetLocTarget(null);
    } catch {
      toast.error("Standort setzen fehlgeschlagen");
    } finally {
      setSavingLoc(false);
    }
  }, [setLocTarget, userPos]);

  const refreshButton = (testid, extra) => (
    <button
      type="button"
      onClick={handleManualRefresh}
      disabled={refreshing}
      data-testid={testid}
      aria-label="Jetzt aktualisieren"
      className={`grid h-12 w-12 place-items-center rounded-2xl bg-white/95 text-slate-700 shadow-lg ring-1 ring-slate-200 backdrop-blur transition-transform hover:scale-[1.05] active:scale-[0.95] disabled:opacity-60 ${extra}`}
    >
      <RefreshCw size={20} className={refreshing ? "animate-spin" : ""} />
    </button>
  );

  const setupButton = (testid, extra) => (
    <a
      href={SETUP_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      aria-label="Setup-Tool öffnen"
      className={`grid h-12 w-12 place-items-center rounded-2xl bg-slate-900 text-white shadow-lg ring-1 ring-slate-900/10 transition-transform hover:scale-[1.05] active:scale-[0.95] ${extra}`}
    >
      <Wrench size={20} />
    </a>
  );

  const coordsValid =
    !!locPos &&
    Number.isFinite(locPos.lat) &&
    Number.isFinite(locPos.lon) &&
    Math.abs(locPos.lat) <= 90 &&
    Math.abs(locPos.lon) <= 180;
  const editValid =
    !editCoords ||
    (Number.isFinite(parseFloat(latStr)) &&
      Math.abs(parseFloat(latStr)) <= 90 &&
      Number.isFinite(parseFloat(lonStr)) &&
      Math.abs(parseFloat(lonStr)) <= 180);

  return (
    <div
      className="relative h-[100dvh] w-full overflow-hidden bg-slate-100"
      data-testid="home-page"
    >
      <HomeMap
        items={computed}
        markerSig={markerSig}
        timeSig={timeSig}
        trailSig={trailSig}
        fitSig={fitSig}
        userPos={userPos}
        onNoLocation={openManualPos}
        overlay={ov.display}
        baseLayer={baseLayer}
        mapApiRef={mapApiRef}
        focusReq={focusReq}
        disableAutoFit={!!alarmParam}
        guideLine={guideLine}
        rotateMs={rotateOverlapS * 1000}
        pickMode={pickLocation}
        onMapPick={handleMapPick}
      />

      {/* "In Bewegung"-Banner oben + dünne Linie zur bewegten Streife (rotiert
          alle 3 s durch, falls mehrere unterwegs sind). */}
      {!alarmParam && currentMoving && (
        <>
          {movePt && (
            <svg
              className="pointer-events-none absolute inset-0 z-[585] h-full w-full"
              data-testid="home-moving-line"
              aria-hidden="true"
            >
              <line
                x1="50%"
                y1="96"
                x2={movePt.x}
                y2={movePt.y}
                stroke={movingColor}
                strokeWidth="2"
                strokeDasharray="5 5"
                strokeLinecap="round"
                opacity="0.9"
              />
              <circle
                cx={movePt.x}
                cy={movePt.y}
                r="4.5"
                fill={movingColor}
                stroke="#fff"
                strokeWidth="1.5"
              />
            </svg>
          )}
          <button
            type="button"
            onClick={() => onFocusObject(currentMoving)}
            data-testid="home-moving-banner"
            title="Zur Streife springen"
            style={{ backgroundColor: movingColor, color: movingText }}
            className="pointer-events-auto absolute left-1/2 top-16 z-[595] flex -translate-x-1/2 items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold shadow-lg ring-1 ring-black/10 transition-transform hover:scale-[1.03] active:scale-95"
          >
            <Navigation size={14} className="-rotate-45" />
            {currentMoving.title || "Streife"} in Bewegung
            {movingList.length > 1 && (
              <span
                style={{ backgroundColor: movingText === "#ffffff" ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.14)" }}
                className="ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums"
              >
                {(moveBannerIdx % movingList.length) + 1}/{movingList.length}
              </span>
            )}
          </button>
        </>
      )}

      {/* Top overlay: title (with burger) + filter cards. Live clock top-right.
          Hidden in the ?alarm=<title> smartphone view. */}
      {!alarmParam && (
      <div className="pointer-events-none absolute inset-x-3 top-3 z-[600] flex items-start justify-between gap-2">
        {/* Left column: title (with burger) + filter cards stacked underneath */}
        <div className="pointer-events-auto flex min-w-0 flex-col items-start gap-2">
          {/* Title card + burger */}
          <div
            className="flex min-w-0 max-w-[17rem] cursor-pointer items-center gap-2 rounded-2xl bg-white/95 px-2.5 py-2.5 shadow-lg ring-1 ring-slate-200 backdrop-blur transition-colors hover:bg-white sm:min-w-[11rem]"
            data-testid="home-title-card"
            role="button"
            tabIndex={0}
            onClick={() => setSidebarOpen((o) => !o)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setSidebarOpen((o) => !o);
              }
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSidebarOpen((o) => !o);
              }}
              aria-label="Objektliste"
              aria-pressed={sidebarOpen}
              data-testid="home-burger-button"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
            >
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div className="min-w-0">
              <span
                className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-600"
                data-testid="home-live-viewers"
              >
                <Radio size={11} /> Live{viewerCount > 0 ? ` (${viewerCount})` : ""}
              </span>
              <span className="block truncate font-display text-lg font-bold leading-tight text-slate-900">
                {data.rootTitle || (data.loading ? "Lädt…" : "Ohne Titel")}
              </span>
            </div>
          </div>

          {/* Filter cards — stacked on the left, directly under the burger menu
              (same spot on desktop + mobile; the side menu may overlay them).
              Hidden in "Karte pur" (focus) mode. */}
          {!focusMode && (
          <div className="flex flex-col items-start gap-2">
          {FILTERS.map(({ key, label, Icon }) => {
            const activeF = filter === key;
            if (key === "tracker") {
              return (
                <div
                  key={key}
                  data-testid="home-filter-tracker-card"
                  className={`flex shrink-0 flex-col gap-1.5 rounded-2xl px-3 py-2 shadow-lg ring-1 backdrop-blur transition-colors ${
                    activeF
                      ? "bg-slate-900 text-white ring-slate-900"
                      : "bg-white/95 text-slate-700 ring-slate-200"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleFilter(key)}
                    data-testid="home-filter-tracker"
                    aria-pressed={activeF}
                    className="flex items-center gap-2 text-left"
                  >
                    <Icon size={17} />
                    <span>
                      <span className="block text-sm font-bold leading-tight">
                        {label} ({counts.tracker})
                      </span>
                      {counts.trackerNoLoc > 0 && (
                        <span
                          className={`block text-[11px] font-semibold leading-tight ${
                            activeF ? "text-white/70" : "text-slate-400"
                          }`}
                          data-testid="home-tracker-noloc"
                        >
                          Ohne Standort: {counts.trackerNoLoc}
                        </span>
                      )}
                    </span>
                  </button>
                  {TRACKER_CATS.some(({ cat }) => catCounts[cat] > 0) && (
                  <div
                    className={`flex flex-col gap-1 border-t pt-1.5 ${
                      activeF ? "border-white/15" : "border-slate-100"
                    }`}
                  >
                    {TRACKER_CATS.filter(({ cat }) => catCounts[cat] > 0).map(({ cat, label: catLabel }) => (
                      <label
                        key={cat}
                        data-testid={`home-cat-${cat}`}
                        className={`flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold leading-tight transition-opacity ${
                          trackerCats[cat] ? "" : "opacity-40"
                        } ${activeF ? "text-white/85" : "text-slate-600"}`}
                      >
                        <input
                          type="checkbox"
                          checked={trackerCats[cat]}
                          onChange={() => toggleCat(cat)}
                          data-testid={`home-cat-${cat}-checkbox`}
                          aria-label={`${catLabel} anzeigen`}
                          className={`h-3.5 w-3.5 shrink-0 rounded ${
                            activeF ? "accent-white" : "accent-slate-700"
                          }`}
                        />
                        <span className="grid h-4 w-4 shrink-0 place-items-center" aria-hidden="true">
                          {cat === "stationary" ? (
                            <Circle
                              size={11}
                              className={`shrink-0 ${
                                activeF ? "fill-white/85 text-white/85" : "fill-green-500 text-green-500"
                              }`}
                            />
                          ) : cat === "moving" ? (
                            <Navigation
                              size={13}
                              className={`shrink-0 -rotate-45 ${
                                activeF ? "fill-white text-white" : "fill-blue-600 text-blue-600"
                              }`}
                            />
                          ) : cat === "nopos" ? (
                            <MapPinOff
                              size={13}
                              className={`shrink-0 ${activeF ? "text-white/80" : "text-slate-400"}`}
                            />
                          ) : (
                            <WifiOff
                              size={13}
                              className={`shrink-0 ${activeF ? "text-white" : "text-amber-500"}`}
                            />
                          )}
                        </span>
                        {catLabel}: {catCounts[cat]}
                      </label>
                    ))}
                  </div>
                  )}
                </div>
              );
            }
            return (
              <button
                key={key}
                onClick={() => toggleFilter(key)}
                data-testid={`home-filter-${key}`}
                aria-pressed={activeF}
                className={`flex shrink-0 items-center gap-2 rounded-2xl px-4 py-2.5 shadow-lg ring-1 backdrop-blur transition-colors ${
                  activeF
                    ? "bg-slate-900 text-white ring-slate-900"
                    : "bg-white/95 text-slate-700 ring-slate-200 hover:bg-white"
                }`}
              >
                <Icon size={17} />
                <span className="text-left">
                  <span className="block text-sm font-bold leading-tight">
                    {label} ({counts.alarm})
                  </span>
                  {counts.alarmNoLoc > 0 && (
                    <span
                      className={`block text-[11px] font-semibold leading-tight ${
                        activeF ? "text-white/70" : "text-slate-400"
                      }`}
                      data-testid="home-alarm-noloc"
                    >
                      Ohne Standort: {counts.alarmNoLoc}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
          </div>
          )}
        </div>

        {/* Sound toggle + live clock — top-right */}
        <div className="pointer-events-auto flex shrink-0 items-start gap-2">
          <button
            type="button"
            onClick={toggleSound}
            aria-pressed={soundOn}
            data-testid="home-sound-toggle"
            aria-label={soundOn ? "Alarmton ausschalten" : "Alarmton einschalten"}
            title={soundOn ? "Alarmton ist an" : "Alarmton ist aus"}
            className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl shadow-lg ring-1 backdrop-blur transition-transform hover:scale-[1.05] active:scale-[0.95] ${
              soundOn
                ? "bg-emerald-600 text-white ring-emerald-600"
                : "bg-white/95 text-slate-500 ring-slate-200"
            }`}
          >
            {soundOn ? <Bell size={19} /> : <BellOff size={19} />}
          </button>
          <div
            className="flex max-w-[11rem] flex-col items-end justify-center rounded-2xl bg-white/95 px-3 py-1.5 shadow-lg ring-1 ring-slate-200 backdrop-blur sm:max-w-none sm:px-4 sm:py-2.5"
            data-testid="home-clock-card"
          >
            <span
              className="font-display text-base font-bold leading-tight tabular-nums text-slate-900 sm:text-lg"
              data-testid="home-clock-time"
            >
              {clockHm}
              <span className="text-[10px] font-semibold text-slate-400 sm:text-[11px]">
                :{clockSec}
              </span>
            </span>
            <span className="text-[10px] font-semibold leading-tight text-slate-400 sm:text-[11px]">
              {clockDate}
            </span>
            {lastAlarm && (
              <span
                className="mt-0.5 block text-right text-[10px] font-bold leading-tight text-red-600 sm:text-[11px]"
                data-testid="home-last-alarm"
              >
                <Siren size={11} className="mb-0.5 mr-1 inline shrink-0" /> Letzter Alarm{" "}
                {lastAlarm.title || "?"} um {clockHM(lastAlarm.sosEpoch)}
              </span>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Pick-my-location mode — tap the map to set the manual position */}
      {pickLocation && (
        <div
          className="pointer-events-auto absolute left-1/2 top-4 z-[1500] flex max-w-[calc(100vw-1.5rem)] -translate-x-1/2 items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-white shadow-lg ring-1 ring-black/20"
          data-testid="pick-location-banner"
        >
          <Crosshair size={15} className="shrink-0 text-sky-300" />
          <span className="truncate text-sm font-semibold">
            Tippe auf die Karte, um deinen Standort zu setzen
          </span>
          <button
            type="button"
            onClick={() => setPickLocation(false)}
            data-testid="pick-location-cancel"
            aria-label="Standort-Auswahl abbrechen"
            className="ml-1 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/20 transition-colors hover:bg-white/30"
          >
            <X size={13} />
          </button>
        </div>
      )}

      {/* Alarm deep-link banner (?alarm=<title>) — target + distance, top-center */}
      {alarmParam && (
        <div
          className="pointer-events-auto absolute left-1/2 top-[4.75rem] z-[660] flex max-w-[calc(100vw-1.5rem)] -translate-x-1/2 items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-white shadow-lg ring-1 ring-red-700/30"
          data-testid="home-alarm-banner"
        >
          <Siren size={15} className="shrink-0" />
          <span className="truncate text-sm font-bold">
            Rettungspunkt {alarmTarget ? alarmTarget.title || alarmParam : alarmParam}
          </span>
          {alarmDistance && (
            <span className="shrink-0 text-[11px] font-semibold text-white/85">
              · {alarmDistance}
            </span>
          )}
          {!alarmTarget && (
            <span className="shrink-0 text-[11px] font-semibold text-white/85">
              · nicht gefunden
            </span>
          )}
          <button
            type="button"
            onClick={exitAlarm}
            data-testid="home-alarm-exit"
            aria-label="Alarm-Ansicht schließen"
            className="ml-1 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/20 transition-colors hover:bg-white/30"
          >
            <X size={13} />
          </button>
        </div>
      )}

      {/* Object list — opens from the burger, below the header, top to bottom */}
      {!alarmParam && sidebarOpen && (
        <div
          className="pointer-events-auto absolute left-3 top-[5rem] z-[650] flex max-h-[calc(100dvh-9.5rem)] w-[min(20rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl bg-white/95 shadow-xl ring-1 ring-slate-200 backdrop-blur"
          data-testid="home-object-list"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <span className="font-display text-sm font-bold text-slate-900">
              {sidebarFilter
                ? SIDEBAR_FILTERS.find((f) => f.key === sidebarFilter)?.label
                : "Alle Objekte"}
            </span>
            <span className="text-[11px] font-semibold text-slate-400">{sidebarItems.length}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 border-b border-slate-100 px-3 py-2">
            <button
              type="button"
              onClick={() => setSidebarFilter(null)}
              data-testid="home-sidebar-filter-all"
              aria-pressed={!sidebarFilter}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                !sidebarFilter
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Alle
            </button>
            {SIDEBAR_FILTERS.map(({ key, label }) => {
              const active = sidebarFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleSidebarFilter(key)}
                  data-testid={`home-sidebar-filter-${key}`}
                  aria-pressed={active}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                    active
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="no-scrollbar divide-y divide-slate-100 overflow-y-auto">
            {sidebarItems.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400">
                {data.loading ? "Lädt…" : "Keine Objekte"}
              </div>
            ) : (
              sidebarItems.map((it) => (
                <ObjectRow
                  key={it.permid}
                  item={it}
                  onSetLocation={openSetLoc}
                  onFocusObject={onFocusObject}
                  onFocusLastKnown={onFocusLastKnown}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Set-location confirmation dialog */}
      <Dialog
        open={!!setLocTarget}
        onOpenChange={(o) => {
          if (!o && !savingLoc) setSetLocTarget(null);
        }}
      >
        <DialogContent
          className="max-w-md"
          data-testid="setloc-dialog"
          onPointerDownOutside={(e) => savingLoc && e.preventDefault()}
          onEscapeKeyDown={(e) => savingLoc && e.preventDefault()}
          onInteractOutside={(e) => savingLoc && e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Standort setzen</DialogTitle>
            <DialogDescription>
              „{setLocTarget?.title || "Objekt"}“ auf deine aktuelle Position setzen?
            </DialogDescription>
          </DialogHeader>
          {locPos && (
            <LocationPreviewMap
              lat={locPos.lat}
              lon={locPos.lon}
              accuracy={locManual ? null : locPos.accuracy}
            />
          )}
          {locPos && !editCoords && (
            <div
              className="flex items-center justify-center gap-2 text-xs font-medium text-slate-500"
              data-testid="setloc-coords"
            >
              <span className="tabular-nums">
                {locPos.lat.toFixed(6)}, {locPos.lon.toFixed(6)}
                {!locManual && locPos.accuracy ? ` · ±${Math.round(locPos.accuracy)} m` : ""}
                {locManual ? " · manuell" : ""}
              </span>
              <button
                type="button"
                onClick={startEditCoords}
                data-testid="setloc-edit-coords"
                aria-label="Koordinaten von Hand eingeben"
                title="Koordinaten von Hand eingeben"
                className="grid h-7 w-7 place-items-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
              >
                <Pencil size={14} />
              </button>
            </div>
          )}
          {locPos && editCoords && (
            <div className="flex items-end gap-2" data-testid="setloc-coords-edit">
              <label className="flex-1 text-left text-[11px] font-semibold text-slate-500">
                Breite
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.000001"
                  value={latStr}
                  onChange={(e) => onCoordChange("lat", e.target.value)}
                  data-testid="setloc-lat-input"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm tabular-nums text-slate-800 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
              </label>
              <label className="flex-1 text-left text-[11px] font-semibold text-slate-500">
                Länge
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.000001"
                  value={lonStr}
                  onChange={(e) => onCoordChange("lon", e.target.value)}
                  data-testid="setloc-lon-input"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm tabular-nums text-slate-800 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
              </label>
              <button
                type="button"
                onClick={resetToGps}
                data-testid="setloc-reset-gps"
                aria-label="Zurück zum GPS-Standort"
                title="Zurück zum GPS-Standort"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white text-blue-600 shadow ring-1 ring-slate-200 transition-transform hover:scale-105 active:scale-95"
              >
                <LocateFixed size={16} />
              </button>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setSetLocTarget(null)}
              disabled={savingLoc}
              data-testid="setloc-cancel"
            >
              Abbrechen
            </Button>
            <Button
              onClick={confirmSetLocation}
              disabled={savingLoc || !coordsValid || !editValid}
              data-testid="setloc-save"
            >
              {savingLoc ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Speichern…
                </>
              ) : (
                "Speichern"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual device position — LAT/LONG fallback when the browser can't locate us */}
      <Dialog open={manualPosOpen} onOpenChange={setManualPosOpen}>
        <DialogContent className="max-w-sm" data-testid="manual-pos-dialog">
          <DialogHeader>
            <DialogTitle>Standort von Hand eingeben</DialogTitle>
            <DialogDescription>
              Gib die Koordinaten deines Geräts von Hand ein. Der Standort bleibt aktiv,
              bis du ihn löschst, und hat Vorrang vor dem GPS.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-2">
            <label className="flex-1 text-left text-[11px] font-semibold text-slate-500">
              Breite (Lat)
              <input
                type="number"
                inputMode="decimal"
                step="0.000001"
                placeholder="52.062"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
                data-testid="manual-pos-lat"
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm tabular-nums text-slate-800 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
            </label>
            <label className="flex-1 text-left text-[11px] font-semibold text-slate-500">
              Länge (Lon)
              <input
                type="number"
                inputMode="decimal"
                step="0.000001"
                placeholder="10.078"
                value={manualLon}
                onChange={(e) => setManualLon(e.target.value)}
                data-testid="manual-pos-lon"
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm tabular-nums text-slate-800 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={startPickLocation}
            data-testid="manual-pos-pick-on-map"
            className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-slate-400 hover:bg-slate-50"
          >
            <Crosshair size={15} className="text-slate-500" />
            Lieber auf der Karte tippen
          </button>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setManualPosOpen(false)}
              data-testid="manual-pos-cancel"
            >
              Später
            </Button>
            <Button onClick={applyManualPos} data-testid="manual-pos-save">
              Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connectivity / partial-data status (bottom-center snackbar) */}
      {(!conn.online || conn.msgErrors > 0) && (
        <div
          className="pointer-events-none absolute bottom-5 left-1/2 z-[680] -translate-x-1/2"
          data-testid="home-conn-status"
        >
          {!conn.online ? (
            <div
              className="flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-white shadow-lg ring-1 ring-red-700/30"
              data-testid="home-conn-offline"
            >
              <WifiOff size={16} />
              <span className="text-sm font-semibold">Keine Verbindung</span>
              {lastOkStr && (
                <span className="text-[11px] font-medium text-white/80">Stand {lastOkStr}</span>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setSidebarFilter("msgerror");
                setSidebarOpen(true);
              }}
              className="pointer-events-auto flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-white shadow-lg ring-1 ring-amber-600/30 transition-transform hover:scale-[1.03] active:scale-95"
              data-testid="home-conn-partial"
              title="Nur nicht geladene Objekte anzeigen"
            >
              <AlertTriangle size={16} />
              <span className="text-sm font-semibold">
                {conn.msgErrors} {conn.msgErrors === 1 ? "Objekt" : "Objekte"} nicht geladen
              </span>
            </button>
          )}
        </div>
      )}

      {/* Layers menu (base map + PDF overlay on/off) */}
      <LayersMenu
        open={layersOpen}
        onClose={() => setLayersOpen(false)}
        baseLayer={baseLayer}
        setBaseLayer={setBaseLayer}
        ov={ov}
      />

      {/* "Karte pur" toggle — rectangular box at bottom-right, left of the zoom
          column. Hides the side filter cards + bottom-left toolbar for a clean
          map (title, clock, bell + zoom/locate stay). */}
      {!alarmParam && (
        <button
          type="button"
          onClick={toggleFocusMode}
          data-testid="home-focus-mode-toggle"
          aria-pressed={focusMode}
          aria-label={focusMode ? "Menüs einblenden" : "Karte pur (Menüs ausblenden)"}
          title={focusMode ? "Menüs einblenden" : "Karte pur – Menüs ausblenden"}
          className={`absolute bottom-4 right-[4.25rem] z-[700] grid h-11 w-14 place-items-center rounded-2xl shadow-lg ring-1 backdrop-blur transition-transform hover:scale-[1.05] active:scale-[0.95] ${
            focusMode
              ? "bg-slate-900 text-white ring-slate-900"
              : "bg-white/95 text-slate-700 ring-slate-200"
          }`}
        >
          {focusMode ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
        </button>
      )}

      {/* Refresh + Layers + Settings + Setup — fixed bottom-left (all viewports).
          Hidden in the ?alarm=<title> smartphone view and in "Karte pur" mode. */}
      {!alarmParam && !focusMode && (
      <div className="absolute bottom-4 left-3 z-[700] flex items-center gap-2">
        {refreshButton("home-refresh-button", "")}
        <button
          type="button"
          onClick={() => setLayersOpen((o) => !o)}
          data-testid="home-layers-button"
          aria-label="Ebenen"
          aria-pressed={layersOpen}
          className={`grid h-12 w-12 place-items-center rounded-2xl shadow-lg ring-1 backdrop-blur transition-transform hover:scale-[1.05] active:scale-[0.95] ${
            layersOpen
              ? "bg-slate-900 text-white ring-slate-900"
              : "bg-white/95 text-slate-700 ring-slate-200"
          }`}
        >
          <Layers size={20} />
        </button>
        <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="home-settings-button"
              aria-label="Einstellungen"
              className="grid h-12 w-12 place-items-center rounded-2xl bg-white/95 text-slate-700 shadow-lg ring-1 ring-slate-200 backdrop-blur transition-transform hover:scale-[1.05] active:scale-[0.95]"
            >
              <SlidersHorizontal size={20} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="start"
            sideOffset={10}
            data-testid="settings-popover"
            className="z-[900] max-h-[80vh] w-72 overflow-y-auto"
          >
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">Einstellungen</p>
              </div>

              <OverlayManager ov={ov} />

              <div>
                <p className="text-xs text-slate-500">Alarmton</p>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5 ring-1 ring-slate-200">
                <div className="flex items-center gap-2">
                  {soundOn ? (
                    <Bell size={16} className="text-emerald-600" />
                  ) : (
                    <BellOff size={16} className="text-slate-400" />
                  )}
                  <span className="text-sm text-slate-700">
                    Alarmton {soundOn ? "an" : "aus"}
                  </span>
                </div>
                <Switch
                  checked={soundOn}
                  onCheckedChange={toggleSound}
                  data-testid="settings-sound-switch"
                  aria-label="Alarmton ein- oder ausschalten"
                />
              </div>

              <div className="space-y-2">
                <Button
                  type="button"
                  onClick={playTestTone}
                  data-testid="test-tone-button"
                  className="w-full gap-2 bg-slate-900 text-white hover:bg-slate-800"
                >
                  <Volume2 size={16} />
                  Testklang abspielen
                </Button>
                {!soundOn && (
                  <p
                    data-testid="test-tone-muted-hint"
                    className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-700 ring-1 ring-amber-200"
                  >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    Alarmton ist aus – der Test wird trotzdem abgespielt.
                  </p>
                )}
              </div>

              <div className="space-y-2 border-t border-slate-100 pt-3">
                <p className="text-xs text-slate-500">Eigener Standort</p>
                <p
                  className="text-[11px] leading-relaxed text-slate-600"
                  data-testid="own-location-status"
                >
                  {manualActive ? (
                    <>
                      Manuell gesetzt
                      {userPos
                        ? ` · ${userPos.lat.toFixed(5)}, ${userPos.lon.toFixed(5)}`
                        : ""}
                    </>
                  ) : userPos ? (
                    "Live-GPS aktiv"
                  ) : (
                    "Kein Standort verfügbar"
                  )}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSettingsOpen(false);
                    openManualPos();
                  }}
                  data-testid="settings-set-location"
                  className="w-full justify-start gap-2"
                >
                  <Pencil size={15} className="text-slate-500" />
                  Standort von Hand setzen
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={startPickLocation}
                  data-testid="settings-pick-location"
                  className="w-full justify-start gap-2"
                >
                  <Crosshair size={15} className="text-slate-500" />
                  Auf Karte tippen
                </Button>
                {manualActive && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearManualPos}
                    data-testid="settings-clear-location"
                    className="w-full justify-start gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 size={15} />
                    Standort löschen
                  </Button>
                )}
                <p className="text-[10px] leading-relaxed text-slate-400">
                  Ein manuell gesetzter Standort bleibt aktiv, bis du ihn löschst –
                  auch wenn das GPS wieder verfügbar ist.
                </p>
              </div>

              <div className="space-y-2 border-t border-slate-100 pt-3">
                <p className="text-xs text-slate-500">Streifen</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm text-slate-700">
                    <LocateFixed size={15} className="text-slate-400" />
                    Stationär-Radius
                  </span>
                  <span
                    className="text-sm font-semibold tabular-nums text-slate-900"
                    data-testid="settings-radius-value"
                  >
                    {radiusM} m
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="200"
                  step="5"
                  value={radiusM}
                  onChange={(e) => setRadiusM(parseInt(e.target.value, 10))}
                  onPointerUp={() => saveRadius(radiusM)}
                  onKeyUp={() => saveRadius(radiusM)}
                  data-testid="settings-radius"
                  className="w-full accent-slate-900"
                  aria-label="Stationär-Radius in Metern"
                />
                <p className="text-[10px] leading-relaxed text-slate-400">
                  Bewegt sich eine Streife über die letzten Pakete um weniger als diesen Radius,
                  gilt sie als „stationär" (GPS-Rauschen wird ignoriert, die Spur ausgeblendet).
                </p>

                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-sm text-slate-700">Bewegungs-Fenster</span>
                  <span
                    className="text-sm font-semibold tabular-nums text-slate-900"
                    data-testid="settings-window-value"
                  >
                    {windowN} Pakete
                  </span>
                </div>
                <input
                  type="range"
                  min="2"
                  max="10"
                  step="1"
                  value={windowN}
                  onChange={(e) => setWindowN(parseInt(e.target.value, 10))}
                  onPointerUp={() => saveWindow(windowN)}
                  onKeyUp={() => saveWindow(windowN)}
                  data-testid="settings-window"
                  className="w-full accent-slate-900"
                  aria-label="Bewegungs-Fenster in Paketen"
                />
                <p className="text-[10px] leading-relaxed text-slate-400">
                  Über wie viele der letzten Pakete die Bewegung gemessen wird – mehr Pakete
                  reagieren träger, weniger empfindlicher.
                </p>

                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-sm text-slate-700">Funkloch-Warnung</span>
                  <span
                    className="text-sm font-semibold tabular-nums text-slate-900"
                    data-testid="settings-signal-value"
                  >
                    {signalMin} Min
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="15"
                  step="1"
                  value={signalMin}
                  onChange={(e) => setSignalMin(parseInt(e.target.value, 10))}
                  onPointerUp={() => saveSignal(signalMin)}
                  onKeyUp={() => saveSignal(signalMin)}
                  data-testid="settings-signal"
                  className="w-full accent-slate-900"
                  aria-label="Funkloch-Warnung nach Minuten"
                />
                <p className="text-[10px] leading-relaxed text-slate-400">
                  Sendet eine Streife länger als diese Zeit keinen Funkspruch (ACT), wird
                  dezent „kein Signal" angezeigt (möglicher Funkloch/kein Empfang).
                </p>

                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-sm text-slate-700">Ohne Verbindung</span>
                  <span
                    className="text-sm font-semibold tabular-nums text-slate-900"
                    data-testid="settings-offline-value"
                  >
                    {offlineMin} Min
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="240"
                  step="5"
                  value={offlineMin}
                  onChange={(e) => setOfflineMin(parseInt(e.target.value, 10))}
                  onPointerUp={() => saveOffline(offlineMin)}
                  onKeyUp={() => saveOffline(offlineMin)}
                  data-testid="settings-offline"
                  className="w-full accent-slate-900"
                  aria-label="Ohne Verbindung nach Minuten"
                />
                <p className="text-[10px] leading-relaxed text-slate-400">
                  Liegt der letzte Check (Meldung/ACT) länger als diese Zeit zurück, gilt das
                  Objekt als „Ohne Verbindung" (im Seitenmenü filterbar).
                </p>

                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-sm text-slate-700">Rotation Überlappung</span>
                  <span
                    className="text-sm font-semibold tabular-nums text-slate-900"
                    data-testid="settings-rotate-value"
                  >
                    {rotateOverlapS === 0 ? "aus" : `${rotateOverlapS} s`}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="1"
                  value={rotateOverlapS}
                  onChange={(e) => setRotateOverlapS(parseInt(e.target.value, 10))}
                  onPointerUp={() => saveRotate(rotateOverlapS)}
                  onKeyUp={() => saveRotate(rotateOverlapS)}
                  data-testid="settings-rotate"
                  className="w-full accent-slate-900"
                  aria-label="Rotation überlappender Symbole in Sekunden"
                />
                <p className="text-[10px] leading-relaxed text-slate-400">
                  Überlappen sich Symbole auf der Karte, wechseln sie sich im Abstand dieser
                  Sekunden im Vordergrund ab (0 = aus).
                </p>

                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-sm text-slate-700">Spur-Länge</span>
                  <span
                    className="text-sm font-semibold tabular-nums text-slate-900"
                    data-testid="settings-trail-value"
                  >
                    {trailPoints} Punkte
                  </span>
                </div>
                <input
                  type="range"
                  min="2"
                  max="40"
                  step="1"
                  value={trailPoints}
                  onChange={(e) => setTrailPoints(parseInt(e.target.value, 10))}
                  onPointerUp={() => saveTrail(trailPoints)}
                  onKeyUp={() => saveTrail(trailPoints)}
                  data-testid="settings-trail"
                  className="w-full accent-slate-900"
                  aria-label="Länge der Bewegungsspur in Positionen"
                />
                <p className="text-[10px] leading-relaxed text-slate-400">
                  Wie viele der letzten Positionen die Bewegungsspur auf der Karte zeigt – mehr
                  Punkte = längere Spur.
                </p>
                {deployInfo?.startedAt && (
                  <p
                    className="mt-1 border-t border-slate-100 pt-2 text-center text-[10px] text-slate-400"
                    data-testid="settings-deploy-time"
                  >
                    Deployment:{" "}
                    {new Date(deployInfo.startedAt).toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    Uhr
                  </p>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {setupButton("home-setup-button", "")}
      </div>
      )}

      {/* Full-page offline veil — greys out the (possibly stale) data and tells
          the user there is no connection; the app re-checks automatically. */}
      {!conn.online && (
        <div
          className="absolute inset-0 z-[2000] flex items-center justify-center bg-slate-900/45 px-4 backdrop-blur-sm"
          data-testid="home-offline-veil"
          role="alert"
        >
          <div className="flex max-w-xs flex-col items-center gap-3 rounded-3xl bg-white/95 px-7 py-8 text-center shadow-2xl ring-1 ring-slate-200">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-red-500">
              <WifiOff size={30} />
            </span>
            <p
              className="font-display text-lg font-bold text-slate-900"
              data-testid="home-offline-title"
            >
              Keine Internetverbindung
            </p>
            <p className="text-sm leading-relaxed text-slate-500">
              Die angezeigten Daten sind evtl. veraltet. Die Verbindung wird alle 10&nbsp;Sekunden
              automatisch neu geprüft.
            </p>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
              <Loader2 size={14} className="animate-spin" /> Verbindung wird geprüft…
            </div>
            <button
              type="button"
              onClick={handleManualRefresh}
              disabled={refreshing}
              data-testid="home-offline-retry"
              className="mt-1 flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white shadow transition-transform hover:scale-[1.03] active:scale-95 disabled:opacity-50"
            >
              <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> Erneut versuchen
            </button>
            {lastOkStr && (
              <p className="text-[11px] text-slate-400">Letzter Stand {lastOkStr}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
